<script lang="ts" setup>
import { AppTypography } from '~/components/base';

// A row is either a link to another page or a button that expands in place.
// `expanded` rotates the chevron in both forms — Contact us is a link row
// (/contact-us) that renders already open, with the slot body below it.
const { label, to, expanded, testid } = defineProps<{
  label: string;
  to?: string;
  expanded?: boolean;
  testid?: string;
}>();

defineEmits<{ click: [] }>();
</script>

<template>
  <li class="support-row">
    <NuxtLink
      v-if="to"
      :to="to"
      :data-testid="testid"
      class="support-row__row"
      @click="$emit('click')"
    >
      <AppTypography type="body">{{ label }}</AppTypography>
      <span class="support-row__action">
        <Icon
          name="solar:alt-arrow-right-linear"
          size="18"
          class="support-row__chevron"
          :class="{ 'support-row__chevron--open': expanded }"
        />
      </span>
    </NuxtLink>
    <button
      v-else
      type="button"
      :aria-expanded="expanded === undefined ? undefined : expanded"
      :data-testid="testid"
      class="support-row__row"
      @click="$emit('click')"
    >
      <AppTypography type="body">{{ label }}</AppTypography>
      <span class="support-row__action">
        <Icon
          name="solar:alt-arrow-right-linear"
          size="24"
          class="support-row__chevron"
          :class="{ 'support-row__chevron--open': expanded }"
        />
      </span>
    </button>

    <!-- Expanded body (Contact us). Rendering is the caller's decision. -->
    <slot />
  </li>
</template>

<style scoped lang="scss">
.support-row {
  &:not(:last-child) > .support-row__row {
    // Figma: borders/secondary (#827b72) — the app's $border-color token is a
    // different, unrelated pale-grey value.
    border-bottom: 1px solid $border-color-tag;
  }

  &__row {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-md;
    min-height: 64px;
    padding: 0;
    border: none;
    background: transparent;
    // Figma: text/primary is this warm near-black, not the navy-palette
    // $text-color-primary token used elsewhere in the app.
    color: #201e1a;
    font: inherit;
    text-align: left;
    text-decoration: none;
    cursor: pointer;

    // Neutralize the global router-link-active background — these rows sit
    // on their own white card and don't need the "current route" highlight.
    &.router-link-active {
      background-color: transparent;
    }
  }

  &__action {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;
    padding: $spacing-sm;
  }

  &__chevron {
    // Figma: the chevron is solid text/primary (#201e1a), not a muted
    // tertiary tone.
    color: #201e1a;
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }
}
</style>
